/**
 * Metadata Redux slice.
 *
 * Holds server-side configuration delivered by the `/metadata` REST endpoint
 * after login.  Split into three logical sub-trees:
 *
 * - **dashboardState** — branding, license, and expiry information shown in
 *   the dashboard and navbar.
 * - **marketWatchState** — available watchlists (default and user-created),
 *   supported backfill periods, and the user's default watchlist selection.
 * - **feedsScripsState** — the full instrument list grouped by feed/segment,
 *   options scrip IDs, and a `symbolScripId` lookup map used to resolve
 *   symbol names from numeric IDs.
 */
import { createSlice } from "@reduxjs/toolkit";

/** Branding and license information shown in the dashboard. */
export interface DashboardState {
  backfillExpiryDetails: {};
  brandLabel: string;
  brandLogoUrl: string;
  brandName: string;
  contactUs: string;
  licenseExpiryDt: string;
  message: string[];
  licenseType: string;
  userName: string;
}

/** Watchlist configuration and instrument catalogue from the server. */
export interface MarketWatchState {
  // allowedSymbolInWatchlist: string;
  defaultWatchlist: {
    id: string;
    name: string;
    scrips: string[];
  }[];
  supportedBackfills: {
    backfillDays: string[];
    candleType: string;
  }[];
  userDefaultWatchlist: {
    watchlistId: string;
  };
  userWatchlist: {
    id: string;
    name: string;
    scrips: string[];
  }[];
}

/** Instrument feed data and lookup maps. */
export interface FeedsScripsState {
  feedsScrips: {
    id: string;
    name: string;
    scrips: {
      id: string;
      symbol: string;
    }[];
  }[];
  optionsScrips: number[];
  symbolScripId: {};
}

/** Indicator licensing state derived from metadata. */
export interface IndicatorListItem {
  id?: string;
  name?: string;
}

export interface IndicatorState {
  /**
   * Master list of indicator IDs the license covers.
   * An empty array means the server has not supplied a list yet —
   * treat as "all allowed" for backward compatibility.
   */
  allowedIndicators: string[];
  /**
   * Indicators that carry an expiry date.
   * An indicator whose `expiryDate` is in the past is disabled in the UI.
   */
  indicatorsWithExpiry: { id: string; expiryDate: string }[];
  /**
   * Raw strategy names/IDs from /metadata (MetaDataDto.indicatorList).
   * The UI uses this as the source of truth for Premium Strategies visibility.
   */
  indicatorList: Array<string | IndicatorListItem>;
  /**
   * Raw per-indicator license expiry map from /metadata.
   * Key can be indicator name or id depending on backend mapping.
   */
  indicatorExpiryDtMap: Record<string, string>;
}

/** Combined metadata state tree. */
export interface MetadataState {
  dashboardState: DashboardState;
  marketWatchState: MarketWatchState;
  feedsScripsState: FeedsScripsState;
  indicatorState: IndicatorState;
}

const initialMetadataState: MetadataState = {
  dashboardState: {
    backfillExpiryDetails: {},
    brandLabel: "",
    brandLogoUrl: "",
    brandName: "",
    contactUs: "",
    licenseExpiryDt: "",
    message: [],
    licenseType: "",
    userName: "",
  },
  marketWatchState: {
    defaultWatchlist: [],
    supportedBackfills: [],
    userDefaultWatchlist: {
      watchlistId: "",
    },
    userWatchlist: [],
  },
  feedsScripsState: {
    feedsScrips: [],
    optionsScrips: [],
    symbolScripId: {},
  },
  indicatorState: {
    allowedIndicators: [],
    indicatorsWithExpiry: [],
    indicatorList: [],
    indicatorExpiryDtMap: {},
  },
};

export const metadataSlice = createSlice({
  name: "metadata",
  initialState: initialMetadataState,
  reducers: {
    metadataReducer: (state, action) => {
      state.dashboardState.backfillExpiryDetails =
        action.payload.backfillExpiryDetails;
      state.dashboardState.brandLabel = action.payload.brandLabel;
      state.dashboardState.brandLogoUrl = action.payload.brandLogoUrl;
      state.dashboardState.brandName = action.payload.brandName;
      state.dashboardState.contactUs = action.payload.contactUs;
      state.dashboardState.licenseExpiryDt = action.payload.licenseExpiryDt;
      state.dashboardState.licenseType = action.payload.licenseType;
      state.dashboardState.message = action.payload.message;
      state.dashboardState.userName = action.payload.userName;

      // state.marketWatchState.allowedSymbolInWatchlist =
      //   action.payload.allowedSymbolInWatchlist;
      state.marketWatchState.defaultWatchlist = action.payload.defaultWatchlist;
      state.marketWatchState.supportedBackfills =
        action.payload.supportedBackfills;
      state.marketWatchState.userDefaultWatchlist =
        action.payload.userDefaultWatchlist;
      state.marketWatchState.userWatchlist = action.payload.userWatchlist;

      const feedsScrips = action.payload.feedsScrips || [];
      state.feedsScripsState.feedsScrips = [...feedsScrips];

      state.feedsScripsState.optionsScrips = feedsScrips
        .filter((feed) => feed.name.includes("OPTIONS"))
        .flatMap((feed) => feed?.scrips || []);
      const symbolValues = {};

      feedsScrips
        .flatMap((scrips) => scrips?.scrips || [])
        .map((symbol) => (symbolValues[parseInt(symbol.id)] = symbol.symbol));

      state.feedsScripsState.symbolScripId = symbolValues;

      // Indicator licensing
      state.indicatorState.allowedIndicators =
        Array.isArray(action.payload.indicators) ? action.payload.indicators : [];
      state.indicatorState.indicatorsWithExpiry =
        Array.isArray(action.payload.indicatorsWithExpiry)
          ? action.payload.indicatorsWithExpiry
          : [];

      state.indicatorState.indicatorList =
        Array.isArray(action.payload.indicatorList)
          ? action.payload.indicatorList.filter(
              (item) =>
                typeof item === "string" ||
                (item && typeof item === "object")
            )
          : [];

      state.indicatorState.indicatorExpiryDtMap =
        action.payload.indicatorExpiryDtMap && typeof action.payload.indicatorExpiryDtMap === "object"
          ? action.payload.indicatorExpiryDtMap
          : {};
    },
    addUserWatchlistReducer: (state, action) => {
      const userWatchlist = {
        id: action.payload.id,
        name: action.payload.name,
        scrips: action.payload.scrips,
      };
      state.marketWatchState.userWatchlist.push(userWatchlist);
    },
    removeUserWatchlistReducer: (state, action) => {
      const index = state.marketWatchState.userWatchlist.findIndex(
        (watchlist) => watchlist.id === action.payload
      );
      state.marketWatchState.userWatchlist.splice(index, 1);
    },
    removeAllUserWatchlistReducer: (state) => {
      state.marketWatchState.userWatchlist = [];
    },
  },
});

export const {
  metadataReducer,
  addUserWatchlistReducer,
  removeUserWatchlistReducer,
  removeAllUserWatchlistReducer,
} = metadataSlice.actions;
export default metadataSlice.reducer;
